import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Download, FileSpreadsheet, ChevronLeft, Loader2 } from 'lucide-react';
import API from '../services/apiBase';

const ScheduleEDetail = ({ runId, onBack }) => {
  const [bases, setBases] = useState([]);
  const [selectedBase, setSelectedBase] = useState(null);
  const [baseDetail, setBaseDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);

  // Fetch summary of all bases
  useEffect(() => {
    const fetchBases = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API}/gl-calcs/schedule-e/detail/${runId}`);
        if (response.ok) {
          const data = await response.json();
          setBases(data.bases || []);
          // Auto-select first base
          if (data.bases && data.bases.length > 0) {
            setSelectedBase(data.bases[0].key);
          }
        }
      } catch (error) {
        console.error('Error fetching Schedule E bases:', error);
      } finally {
        setLoading(false);
      }
    };

    if (runId) {
      fetchBases();
    }
  }, [runId]);

  // Fetch detail when base is selected
  useEffect(() => {
    const fetchBaseDetail = async () => {
      if (!selectedBase) return;

      try {
        setDetailLoading(true);
        const response = await fetch(`${API}/gl-calcs/schedule-e/detail/${runId}?base_key=${selectedBase}`);
        if (response.ok) {
          const data = await response.json();
          setBaseDetail(data);
        }
      } catch (error) {
        console.error('Error fetching base detail:', error);
      } finally {
        setDetailLoading(false);
      }
    };

    fetchBaseDetail();
  }, [runId, selectedBase]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '—';
    return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleExport = async () => {
    try {
      // Open export URL in new window for download
      window.open(`${API}/gl-calcs/export/schedule-e-detail/${runId}`, '_blank');
    } catch (error) {
      console.error('Error exporting Schedule E:', error);
    }
  };

  // Group bases by category for the tab list
  const fringeBases = bases.filter(b => b.key.startsWith('fringe_'));
  const divisionBases = bases.filter(b => b.key.startsWith('oh_'));
  const gaBases = bases.filter(b => b.key.startsWith('ga_') || b.key === 'smh_base');

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2">Loading Schedule E detail...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-blue-600" />
              Schedule E - Account Breakdowns
            </CardTitle>
          </div>
          <Button onClick={handleExport} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            Export All Tabs
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs value={selectedBase} onValueChange={setSelectedBase}>
          {/* Category Labels */}
          <div className="mb-4 space-y-4">
            {/* Fringe Bases */}
            {fringeBases.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1" style={{ background: '#E2EFDA', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>
                  Fringe Benefit Bases
                </div>
                <TabsList className="flex flex-wrap gap-1">
                  {fringeBases.map(base => (
                    <TabsTrigger key={base.key} value={base.key} className="text-xs">
                      {base.label.replace(' Base', '')}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            )}

            {/* Division OH Bases */}
            {divisionBases.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1" style={{ background: '#DDEBF7', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>
                  Division Overhead Bases
                </div>
                <TabsList className="flex flex-wrap gap-1">
                  {divisionBases.map(base => (
                    <TabsTrigger key={base.key} value={base.key} className="text-xs">
                      {base.label.replace(' OH Base', '').replace('Division ', 'Div ')}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            )}

            {/* G&A Bases */}
            {gaBases.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1" style={{ background: '#FCE4D6', padding: '0.5rem 0.75rem', borderRadius: '4px' }}>
                  G&A Base Components
                </div>
                <TabsList className="flex flex-wrap gap-1">
                  {gaBases.map(base => (
                    <TabsTrigger key={base.key} value={base.key} className="text-xs">
                      {base.label.replace('G&A Base - ', '').replace(' Base', '')}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </div>
            )}
          </div>

          {/* Content for all bases */}
          {bases.map(base => (
            <TabsContent key={base.key} value={base.key}>
              {detailLoading ? (
                <div className="py-8 flex items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                  <span className="ml-2">Loading accounts...</span>
                </div>
              ) : baseDetail && selectedBase === base.key ? (
                <div>
                  {/* Base Summary */}
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-lg mb-1">{baseDetail.label}</h3>
                    <p className="text-sm text-gray-600 mb-2">{baseDetail.description}</p>
                    <div className="flex gap-6 mt-3">
                      <div>
                        <span className="text-xs text-gray-500 uppercase">Total</span>
                        <div className="text-xl font-bold text-blue-600">{formatCurrency(baseDetail.total)}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 uppercase">Accounts</span>
                        <div className="text-xl font-bold text-gray-700">{baseDetail.account_count}</div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 uppercase">Reference</span>
                        <div className="text-sm font-medium text-gray-600">{baseDetail.ref}</div>
                      </div>
                    </div>
                  </div>

                  {/* Account Table */}
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-900 text-white">
                          <th className="px-4 py-3 text-left font-semibold">Lookup Code</th>
                          <th className="px-4 py-3 text-left font-semibold">Pool Code</th>
                          <th className="px-4 py-3 text-left font-semibold">Account</th>
                          <th className="px-4 py-3 text-right font-semibold">Expenses per G/L</th>
                          <th className="px-4 py-3 text-right font-semibold">Expenses Claimed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {baseDetail.accounts && baseDetail.accounts.map((acc, idx) => (
                          <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-4 py-2 font-mono text-xs">{acc.lookup_code}</td>
                            <td className="px-4 py-2">{acc.pool_code}</td>
                            <td className="px-4 py-2 font-mono text-xs">{acc.account}</td>
                            <td className="px-4 py-2 text-right font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatCurrency(acc.expenses_per_gl)}
                            </td>
                            <td className="px-4 py-2 text-right font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                              {formatCurrency(acc.expenses_claimed)}
                            </td>
                          </tr>
                        ))}
                        {/* Total Row */}
                        <tr className="bg-amber-50 font-bold border-t-2 border-gray-300">
                          <td className="px-4 py-3" colSpan={3}>Total {baseDetail.label}</td>
                          <td className="px-4 py-3 text-right font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(baseDetail.total)}
                          </td>
                          <td className="px-4 py-3 text-right font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(baseDetail.total)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-8 text-center text-gray-500">
                  Select a base to view account details
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ScheduleEDetail;
